import { Box, Button, Group, Text, Tooltip } from '@mantine/core';
import { MantineReactTable, MRT_TablePagination, useMantineReactTable } from 'mantine-react-table';
import type {
    MRT_ColumnDef,
    MRT_DensityState,
    MRT_RowData,
    MRT_RowSelectionState,
    MRT_SortingState,
    MRT_VisibilityState,
} from 'mantine-react-table';
import React from 'react';

export type SelectionAction = {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    color?: string;
    variant?: 'subtle' | 'outline' | 'filled' | 'light';
    startIcon?: React.ReactNode;
};

export type ColumnType = 'text' | 'number' | 'currency' | 'percentage' | 'date' | 'status' | 'thumbnail' | 'actions';

export type TableColumn<T> = {
    name: string;
    alt?: string;
    render: (row: T, selected: boolean) => React.ReactNode;
    sort?: (left: T, right: T) => number;
    defaultDirection?: 'asc' | 'desc';
    width?: number;
    align?: 'left' | 'right' | 'center';
    hidden?: boolean;
    valueGetter?: (row: T) => string | number | null | undefined;

    type?: ColumnType;

    footer?: (data: T[]) => React.ReactNode;
    grow?: boolean | number;
    enableResizing?: boolean;
};

export type TableProps<T extends MRT_RowData> = {
    id: (row: T) => string | number;
    columns: TableColumn<T>[];
    data: T[];
    defaultSortColumn?: string;
    className?: string;
    pagination?: {
        pageSize?: number;
        showPagination?: boolean;
    };
    selection?: {
        selectedIds: Set<string | number>;
        onSelectionChange: (selectedIds: Set<string | number>) => void;
        isSelectable?: (row: T) => boolean;
        actions?: SelectionAction[];
    };
    storageKey?: string;
    loading?: boolean;
    density?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    striped?: boolean;
};

// Resolve alignment from type when align is not explicitly set
function resolveAlign(column: { align?: string; type?: ColumnType }): 'left' | 'right' | 'center' {
    if (column.align) return column.align as 'left' | 'right' | 'center';
    switch (column.type) {
        case 'number':
        case 'currency':
        case 'percentage':
            return 'right';
        case 'thumbnail':
        case 'actions':
            return 'center';
        default:
            return 'left';
    }
}

// Helper function to extract text from rendered content for filtering
function extractTextFromNode(node: any): string {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (React.isValidElement(node)) {
        const nodeProps = node.props as any;
        if (nodeProps.children) {
            if (Array.isArray(nodeProps.children)) {
                return nodeProps.children.map(extractTextFromNode).filter(Boolean).join(' ');
            }
            return extractTextFromNode(nodeProps.children);
        }
    }
    if (Array.isArray(node)) {
        return node.map(extractTextFromNode).filter(Boolean).join(' ');
    }
    return '';
}

export function Table<T extends MRT_RowData>({
    id,
    columns,
    data,
    defaultSortColumn,
    className,
    pagination,
    selection,
    storageKey,
    loading = false,
    density = 'xs',
    striped = false,
}: TableProps<T>) {
    const pageSize = pagination?.pageSize ?? 10;
    const storageKeyFull = storageKey ? `table-state-${storageKey}` : null;

    // Load persisted state from localStorage
    const loadPersistedState = React.useCallback(() => {
        if (!storageKeyFull) return null;
        try {
            const stored = localStorage.getItem(storageKeyFull);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }, [storageKeyFull]);

    const persistedState = React.useMemo(() => loadPersistedState(), [loadPersistedState]);

    // Column visibility state — persisted overrides take precedence over column defaults
    const defaultVisibility = React.useMemo(() => {
        const defaults: MRT_VisibilityState = {};
        for (const col of columns) {
            if (col.hidden) defaults[col.name] = false;
        }
        return defaults;
    }, [columns]);

    const [columnVisibility, setColumnVisibility] = React.useState<MRT_VisibilityState>(
        persistedState?.columnVisibility || defaultVisibility,
    );

    // Sorting state — persisted
    const initialSorting = React.useMemo((): MRT_SortingState => {
        if (!defaultSortColumn) return [];
        const column = columns.find((col) => col.name === defaultSortColumn);
        if (!column) return [];
        return [{ id: defaultSortColumn, desc: (column.defaultDirection || 'asc') === 'desc' }];
    }, [defaultSortColumn, columns]);

    const [sorting, setSorting] = React.useState<MRT_SortingState>(persistedState?.sorting || initialSorting);

    // Persist state helper
    const persistState = React.useCallback(
        (key: string, value: unknown) => {
            if (!storageKeyFull) return;
            try {
                const current = JSON.parse(localStorage.getItem(storageKeyFull) || '{}');
                localStorage.setItem(storageKeyFull, JSON.stringify({ ...current, [key]: value }));
            } catch {
                // ignore
            }
        },
        [storageKeyFull],
    );

    // Persist column visibility changes
    const handleColumnVisibilityChange = React.useCallback(
        (updater: MRT_VisibilityState | ((prev: MRT_VisibilityState) => MRT_VisibilityState)) => {
            setColumnVisibility((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                persistState('columnVisibility', next);
                return next;
            });
        },
        [persistState],
    );

    // Persist sorting changes
    const handleSortingChange = React.useCallback(
        (updater: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState)) => {
            setSorting((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                persistState('sorting', next);
                return next;
            });
        },
        [persistState],
    );

    // Convert selection Set to MRT Record<string, boolean>
    const rowSelection = React.useMemo((): MRT_RowSelectionState => {
        if (!selection) return {};
        const result: MRT_RowSelectionState = {};
        for (const selectedId of selection.selectedIds) {
            result[String(selectedId)] = true;
        }
        return result;
    }, [selection]);

    // Check if any column has a footer
    const hasFooter = React.useMemo(() => columns.some((col) => col.footer), [columns]);

    // Convert custom columns to MRT column definitions
    const mrtColumns = React.useMemo((): MRT_ColumnDef<T>[] => {
        const flatColumns: MRT_ColumnDef<T>[] = columns.map((column) => {
            const align = resolveAlign(column);
            const isRightAligned = align === 'right';
            const skipGlobalFilter = column.type === 'thumbnail' || column.type === 'actions';

            const cellStyle: React.CSSProperties = {
                textAlign: align,
                ...(isRightAligned ? { fontVariantNumeric: 'tabular-nums' } : {}),
            };

            const mrtCol: MRT_ColumnDef<T> = {
                id: column.name,
                header: column.name,
                accessorFn: (row: T) => {
                    if (column.valueGetter) {
                        return column.valueGetter(row);
                    }
                    try {
                        const content = column.render(row, false);
                        return extractTextFromNode(content);
                    } catch {
                        return '';
                    }
                },
                Cell: ({ row }) => {
                    const isSelected = selection?.selectedIds.has(row.id as string | number) ?? false;
                    return column.render(row.original, isSelected);
                },
                ...(column.alt
                    ? {
                          Header: () => (
                              <Tooltip label={column.alt} withArrow>
                                  <Text span inherit style={{ cursor: 'help' }}>
                                      {column.name}
                                  </Text>
                              </Tooltip>
                          ),
                      }
                    : {}),
                mantineTableHeadCellProps: { style: { textAlign: align } },
                mantineTableBodyCellProps: { style: cellStyle },
                size: column.width || 150,
                grow: column.grow ?? !column.width,
                enableSorting: !!column.sort,
                sortingFn: column.sort ? (rowA, rowB) => column.sort!(rowA.original, rowB.original) : undefined,
                sortDescFirst: column.defaultDirection === 'desc',
                enableGlobalFilter: !skipGlobalFilter,
                enableResizing: column.enableResizing !== false,
            };

            if (column.footer) {
                const footerFn = column.footer;
                mrtCol.Footer = () => (
                    <Box
                        style={{ textAlign: align, ...(isRightAligned ? { fontVariantNumeric: 'tabular-nums' } : {}) }}
                    >
                        {footerFn(data)}
                    </Box>
                );
            }

            return mrtCol;
        });

        return flatColumns;
    }, [columns, selection?.selectedIds, data]);

    const handleClearSelection = React.useCallback(() => {
        if (selection) {
            selection.onSelectionChange(new Set());
        }
    }, [selection]);

    const table = useMantineReactTable<T>({
        columns: mrtColumns,
        data: data ?? [],
        getRowId: (row) => String(id(row)),
        enableRowSelection: !!selection,
        enableSelectAll: !!selection,
        enableRowActions: false,
        enableColumnActions: true,
        enableGlobalFilter: true,
        enableColumnFilters: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: true,
        enablePagination: true,
        manualPagination: false,
        paginationDisplayMode: 'pages',
        positionGlobalFilter: 'left',
        layoutMode: 'grid',
        // Sticky header
        enableStickyHeader: true,
        enableColumnPinning: false,
        // Column resizing
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        // Table footer for totals
        enableTableFooter: hasFooter,
        state: {
            isLoading: loading,
            rowSelection,
            columnVisibility,
            sorting,
            density: density as MRT_DensityState,
        },
        onRowSelectionChange: (updater) => {
            if (!selection) return;
            const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
            const newIds = new Set<string | number>(
                Object.entries(newSelection)
                    .filter(([, selected]) => selected)
                    .map(([rowId]) => rowId),
            );
            selection.onSelectionChange(newIds);
        },
        onColumnVisibilityChange: handleColumnVisibilityChange,
        onSortingChange: handleSortingChange,
        enableMultiRowSelection: true,
        ...(selection?.isSelectable
            ? {
                  enableRowSelection: (row) => selection.isSelectable!(row.original),
              }
            : {}),
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize,
            },
            showGlobalFilter: true,
        },
        renderTopToolbarCustomActions: () => null,
        mantineSearchTextInputProps: {
            placeholder: 'Search...',
        },
        mantinePaperProps: {
            withBorder: true,
            radius: 'sm',
            className,
        },
        mantineTableContainerProps: {
            style: { overflowX: 'auto', maxHeight: '70vh' },
        },
        mantineTableProps: {
            ...(striped ? { striped: 'even' } : {}),
            highlightOnHover: true,
            fz: 'sm',
        },
        mantineTableHeadCellProps: {
            fz: 'sm',
            py: 6,
            px: 8,
        },
        mantineTableBodyCellProps: {
            py: 4,
            px: 8,
        },
        renderBottomToolbar: ({ table }) => {
            const selectedCount = Object.values(table.getState().rowSelection).filter(Boolean).length;
            const hasActions = selection?.actions && selection.actions.length > 0;

            return (
                <Group justify="space-between" p="xs" wrap="wrap">
                    {selectedCount > 0 ? (
                        <Group gap="xs">
                            <Text size="sm" fw={500} c="dimmed">
                                {selectedCount} selected
                            </Text>
                            <Button size="xs" variant="subtle" onClick={handleClearSelection}>
                                Clear selection
                            </Button>
                            {hasActions &&
                                selection.actions!.map((action, index) => (
                                    <Button
                                        key={index}
                                        size="xs"
                                        onClick={action.onClick}
                                        disabled={action.disabled}
                                        color={action.color}
                                        variant={action.variant}
                                        leftSection={action.startIcon}
                                    >
                                        {action.label}
                                    </Button>
                                ))}
                        </Group>
                    ) : (
                        <Box />
                    )}
                    <MRT_TablePagination table={table} />
                </Group>
            );
        },
    });

    return <MantineReactTable table={table} />;
}
